'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Modal from '@/components/ui/Modal';
import PetFoodCalculator from './tools/PetFoodCalculator';
import CompatibilityQuiz from './tools/CompatibilityQuiz';
import CostEstimator from './tools/CostEstimator';

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

interface InteractiveToolsProps {
  tools: Tool[];
}

const InteractiveTools = ({ tools }: InteractiveToolsProps) => {
  const [activeTool, setActiveTool] = useState<Tool | null>(null);

  const handleToolClick = (tool: Tool) => {
    setActiveTool(tool);
  };

  const renderToolContent = () => {
    if (!activeTool) return null;

    switch (activeTool.id) {
      case 'tool-1': // Pet Food Calculator
        return <PetFoodCalculator />;
      case 'tool-3': // Compatibility Quiz
        return <CompatibilityQuiz />;
      case 'tool-4': // Cost Estimator
        return <CostEstimator />;
      default:
        return (
          <div className="text-center py-12">
            <p className="text-muted-foreground">This tool is coming soon!</p>
          </div>
        );
    }
  };

  return (
    <>
      <div className="bg-card rounded-xl shadow-soft p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="CalculatorIcon" size={24} variant="outline" className="text-primary" />
          <h3 className="text-xl font-headline font-semibold text-foreground">Interactive Tools</h3>
        </div>

        <div className="space-y-4">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => handleToolClick(tool)}
              className="w-full flex items-start space-x-4 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-all duration-300 text-left group"
            >
              <div className={`p-3 rounded-lg ${tool.color} flex-shrink-0`}>
                <Icon name={tool.icon as any} size={24} variant="outline" className="text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                  {tool.title}
                </h4>
                <p className="text-sm text-muted-foreground">{tool.description}</p>
              </div>
              <Icon
                name="ChevronRightIcon"
                size={20}
                variant="outline"
                className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1"
              />
            </button>
          ))}
        </div>
      </div>

      <Modal
        isOpen={!!activeTool}
        onClose={() => setActiveTool(null)}
        title={activeTool?.title || ''}
      >
        {renderToolContent()}
      </Modal>
    </>
  );
};

export default InteractiveTools;